import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

type LifecycleStage = 'lead' | 'qualified' | 'customer' | 'past_customer' | 'dead';

interface Contact {
  id: number;
  display_name: string;
  primary_phone: string | null;
  primary_email: string | null;
  company: string | null;
  hubspot_id: string | null;
  odoo_partner_id: number | null;
  lifecycle_stage: LifecycleStage | null;
  contact_properties: Record<string, unknown>;
  is_active: boolean;
  soft_deleted_at: string | null;
  soft_deleted_by: string | null;
  created_source: string | null;
  last_updated_source: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

const LIFECYCLE_VALUES: LifecycleStage[] = ['lead', 'qualified', 'customer', 'past_customer', 'dead'];

interface FormState {
  display_name: string;
  primary_phone: string;
  primary_email: string;
  company: string;
  lifecycle_stage: '' | LifecycleStage;
  hubspot_id: string;
  odoo_partner_id: string;
}

const EMPTY_FORM: FormState = {
  display_name: '',
  primary_phone: '',
  primary_email: '',
  company: '',
  lifecycle_stage: '',
  hubspot_id: '',
  odoo_partner_id: '',
};

function fromContact(c: Contact): FormState {
  return {
    display_name: c.display_name,
    primary_phone: c.primary_phone ?? '',
    primary_email: c.primary_email ?? '',
    company: c.company ?? '',
    lifecycle_stage: c.lifecycle_stage ?? '',
    hubspot_id: c.hubspot_id ?? '',
    odoo_partner_id: c.odoo_partner_id == null ? '' : String(c.odoo_partner_id),
  };
}

export function ContactForm({ mode }: { mode: 'create' | 'edit' }): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(mode === 'edit');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [clientError, setClientError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await fetch(`/api/contacts/${id}`, { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          setLoadError(body.error ?? `Request failed: ${res.status}`);
          setLoading(false);
          return;
        }
        const payload = (await res.json()) as { contact: Contact };
        setForm(fromContact(payload.contact));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, id]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  }

  function validate(): string | null {
    if (form.display_name.trim().length === 0) {
      return 'Display name is required.';
    }
    if (form.odoo_partner_id.trim().length > 0 && !/^\d+$/.test(form.odoo_partner_id.trim())) {
      return 'Odoo partner ID must be a positive integer.';
    }
    return null;
  }

  function buildBody(): Record<string, unknown> {
    const body: Record<string, unknown> = {
      display_name: form.display_name.trim(),
    };
    body.primary_phone = form.primary_phone.trim() === '' ? null : form.primary_phone.trim();
    body.primary_email = form.primary_email.trim() === '' ? null : form.primary_email.trim();
    body.company = form.company.trim() === '' ? null : form.company.trim();
    body.lifecycle_stage = form.lifecycle_stage === '' ? null : form.lifecycle_stage;
    body.hubspot_id = form.hubspot_id.trim() === '' ? null : form.hubspot_id.trim();
    body.odoo_partner_id =
      form.odoo_partner_id.trim() === '' ? null : Number(form.odoo_partner_id.trim());
    return body;
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setServerError(null);
    setFieldErrors({});
    const clientMsg = validate();
    if (clientMsg) {
      setClientError(clientMsg);
      return;
    }
    setClientError(null);
    setSubmitting(true);

    try {
      const body = buildBody();
      const url = mode === 'create' ? '/api/contacts' : `/api/contacts/${id}`;
      const method = mode === 'create' ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as {
          error?: string;
          field_errors?: Record<string, string>;
        };
        if (errBody.field_errors && typeof errBody.field_errors === 'object') {
          setFieldErrors(errBody.field_errors);
        }
        setServerError(errBody.error ?? `Request failed: ${res.status}`);
        setSubmitting(false);
        return;
      }
      const payload = (await res.json()) as { contact: Contact };
      setSubmitting(false);
      navigate(`/contacts/${payload.contact.id}`);
    } catch (err) {
      setServerError((err as Error).message);
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl bg-white ring-1 ring-slate-200 p-8 text-sm text-slate-500">Loading…</div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <Link to="/contacts" className="text-sm text-slate-600 hover:underline">← Back to contacts</Link>
        <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-5">
          <p className="text-sm font-medium text-rose-900">Failed to load contact</p>
          <p className="mt-1 text-sm text-rose-800">{loadError}</p>
        </div>
      </div>
    );
  }

  const backLink = mode === 'edit' && id ? `/contacts/${id}` : '/contacts';

  return (
    <div className="space-y-4 max-w-2xl">
      <Link to={backLink} className="text-sm text-slate-600 hover:underline">← Back</Link>
      <h1 className="text-xl font-semibold tracking-tight text-slate-900">
        {mode === 'create' ? 'New contact' : 'Edit contact'}
      </h1>

      {(clientError || serverError) && (
        <section className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-4">
          <p className="text-sm font-medium text-rose-900">{clientError ?? serverError}</p>
        </section>
      )}

      <form onSubmit={(e) => void onSubmit(e)} className="rounded-xl bg-white ring-1 ring-slate-200 p-5 space-y-4">
        <Field
          label="Display name"
          required
          error={fieldErrors.display_name ?? null}
        >
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => update('display_name', e.target.value)}
            required
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Primary phone" error={fieldErrors.primary_phone ?? null}>
            <input
              type="tel"
              value={form.primary_phone}
              onChange={(e) => update('primary_phone', e.target.value)}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </Field>

          <Field label="Primary email" error={fieldErrors.primary_email ?? null}>
            <input
              type="email"
              value={form.primary_email}
              onChange={(e) => update('primary_email', e.target.value)}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </Field>
        </div>

        <Field label="Company" error={fieldErrors.company ?? null}>
          <input
            type="text"
            value={form.company}
            onChange={(e) => update('company', e.target.value)}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </Field>

        <Field label="Lifecycle stage" error={fieldErrors.lifecycle_stage ?? null}>
          <select
            value={form.lifecycle_stage}
            onChange={(e) => update('lifecycle_stage', e.target.value as '' | LifecycleStage)}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            <option value="">— none —</option>
            {LIFECYCLE_VALUES.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="HubSpot ID" error={fieldErrors.hubspot_id ?? null}>
            <input
              type="text"
              value={form.hubspot_id}
              onChange={(e) => update('hubspot_id', e.target.value)}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </Field>

          <Field label="Odoo partner ID" error={fieldErrors.odoo_partner_id ?? null}>
            <input
              type="text"
              inputMode="numeric"
              value={form.odoo_partner_id}
              onChange={(e) => update('odoo_partner_id', e.target.value)}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:bg-slate-400"
          >
            {submitting ? 'Saving…' : mode === 'create' ? 'Create contact' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required = false,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error: string | null;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-rose-700">{error}</p>}
    </div>
  );
}
